import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './VideoTextPanel.module.scss';
import cx from 'classnames';
import useObjectSimulation from '../../hooks/useObjectSimulation';
import { CustomButton } from '../Button/Button';
import Icon from '../Icon/Icon';
import { useTextPanelContext } from '../../context/TextPanelContext';
import { PANEL_TYPE } from 'engage/routes/CompanySettings/components/TextPanels/constants';
import { useTranslation } from 'react-i18next';

const TEXT_PANEL_TRANSITION_TIME = 500;

const VideoTextPanel = ({
  id,
  parentNode,
  textPanelsArray = [],
  videoSecondsProgress,
  multiplyMode = false,
  templates = [],
}) => {
  const [activeTextPanel, setActiveTextPanel] = useState(null);
  const [adjustedTemplate, setAdjustedTemplate] = useState({});
  const [opacity, setOpacity] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [updateSimulation, setUpdateSimulation] = useState(false);

  const { t } = useTranslation();

  const { isTextPanelEditor, topText, bottomText, currentTemplate, removeTextPanel, textPanelsTemplatesList } = useTextPanelContext();

  const localTemplate = useMemo(() =>
    isTextPanelEditor ? currentTemplate : templates?.find(item => activeTextPanel?.templateId ? item.id === activeTextPanel?.templateId : item.active) ?? textPanelsTemplatesList?.find(item => activeTextPanel?.templateId ? item.id === activeTextPanel?.templateId : item.active),
    [templates, activeTextPanel, isTextPanelEditor, currentTemplate?.id]);

  const { objectPosition, recalculateVuToPx, loading } = useObjectSimulation({
    parentNode,
    id,
    margin_x: adjustedTemplate?.marginX,
    margin_y: adjustedTemplate?.marginY,
    position: adjustedTemplate?.position,
    mode: 'textPanel',
    updateSimulation,
    multiplyMode,
  });

  const recalculatePadding = useCallback(paddingString => {
    return paddingString
      ?.split(' ')
      .map(item => `${recalculateVuToPx(parseInt(item))}px`)
      .join(' ');
  }, [recalculateVuToPx]);

  useEffect(() => {
    if (localTemplate) {
      const template = structuredClone(localTemplate);
      ['containerStyles', 'topLabelStyles', 'bottomLabelStyles'].forEach(field => {
        if (template[field]) {
          Object.entries(template[field]).forEach(([style, value]) => {
            if (!value) {
              return;
            }
            template[field][style] = ['fontSize', 'borderRadius', 'borderWidth', 'marginBottom'].includes(style)
              ? recalculateVuToPx(template[field][style])
              : style === 'padding'
                ? recalculatePadding(template[field][style])
                : value;
          });
        }
      });
      setAdjustedTemplate(template);
    }
  }, [localTemplate?.id, objectPosition]);

  useEffect(() => {
    setUpdateSimulation(true);
    setTimeout(() => {
      setUpdateSimulation(false);
    }, 50);
  }, [currentIndex]);

  useEffect(() => {
    if (!isTextPanelEditor && textPanelsArray) {
      if (videoSecondsProgress > 10) {
        const textPanel = textPanelsArray.find(item => videoSecondsProgress >= item.start && videoSecondsProgress <= item.end);
        setCurrentIndex(textPanelsArray.indexOf(textPanel));
        if (textPanel?.text) {
          let newOpacity = 0;
          if (videoSecondsProgress - textPanel.start < TEXT_PANEL_TRANSITION_TIME) {
            newOpacity = Math.min(1, ((videoSecondsProgress - textPanel.start) * 0.2) / 100);
          } else {
            newOpacity = 0;
            if (videoSecondsProgress >= textPanel.end - TEXT_PANEL_TRANSITION_TIME) {
              newOpacity = Math.max(0, ((textPanel.end - videoSecondsProgress) * 0.2) / 100);
            } else {
              newOpacity = 1;
            }
          }
          setOpacity(Math.min(newOpacity, adjustedTemplate?.containerStyles?.opacity ?? 1));
          setActiveTextPanel({ ...textPanel.text });
        } else {
          setActiveTextPanel(null);
          setOpacity(0);
        }
      } else {
        setActiveTextPanel(null);
        setOpacity(0);
      }
    }
  }, [videoSecondsProgress, textPanelsArray]);

  useEffect(() => {
    if (isTextPanelEditor) {
      if ((topText && topText?.trim()) || (bottomText && bottomText?.trim())) {
        setActiveTextPanel(prevState => ({ ...prevState, topText, bottomText }));
        setOpacity(adjustedTemplate?.containerStyles?.opacity ?? 1);
      } else {
        setActiveTextPanel(null);
        setOpacity(0);
      }
    }
  }, [isTextPanelEditor, topText, bottomText, adjustedTemplate]);

  return textPanelsArray?.length || isTextPanelEditor ? (
    <div className={cx({ [styles.hide]: loading })}>
      <div
        className={cx(styles.textPanelContainer, {
          [styles.twoPanels]: adjustedTemplate?.type === PANEL_TYPE.TWO_PANELS.type,
          [styles.overPlayer]: isTextPanelEditor,
        })}
        style={{ ...adjustedTemplate?.containerStyles, ...objectPosition, opacity: Object.keys(objectPosition).length ? opacity : 0 }}
      >
        {activeTextPanel?.topText?.length ? <div style={adjustedTemplate?.topLabelStyles}>{activeTextPanel?.topText}</div> : null}
        {activeTextPanel?.bottomText?.length ? <div style={adjustedTemplate?.bottomLabelStyles}>{activeTextPanel?.bottomText}</div> : null}
        {isTextPanelEditor ? (
          <CustomButton className={styles.deleteLogoBtn} onClick={removeTextPanel} variant="link" ariaLabel={t('general.close')}>
            <Icon src="close" className={styles.deleteLogoIcon} />
          </CustomButton>
        ) : null}
      </div>
    </div>
  ) : null;
};

export default VideoTextPanel;
